/**
 * Lead-memory STORAGE primitives.
 *
 * Deliberately dependency-free (no consent import) so `src/lib/consent.ts` can
 * purge stored contact details centrally without a circular import. Consent
 * decisions live in the callers; this module only owns the bytes.
 *
 * Rules baked in here:
 *   - FIXED 30-day lifetime measured from the FIRST write of a cache. A later
 *     autosave of the same cache updates the values but never extends the
 *     clock (no rolling expiry).
 *   - Malformed, legacy, or future-dated envelopes are unusable and removed.
 */

export const LEAD_MEMORY_KEY = "mb:lead-memory:v2";
export const LEAD_MEMORY_LEGACY_KEY = "mb:lead-memory:v1";
export const LEAD_MEMORY_TTL_MS = 30 * 24 * 60 * 60 * 1000; // fixed 30 days
export const LEAD_MEMORY_ENVELOPE_VERSION = 2;

export interface LeadMemory {
  firstName?: string;
  lastName?: string;
  fullName?: string; // single-field name (e.g. LiveText)
  email?: string;
  phone?: string;
  address?: string;
  zip?: string;
}

export interface LeadMemoryEnvelope {
  v: number;
  /** First write of this cache — the fixed 30-day clock starts here. */
  savedAt: number;
  data: LeadMemory;
}

const FIELDS: (keyof LeadMemory)[] = [
  "firstName",
  "lastName",
  "fullName",
  "email",
  "phone",
  "address",
  "zip",
];

export function leadMemoryStorageAvailable(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

/** Delete every stored copy (current + legacy). Safe to call at any time. */
export function purgeLeadMemoryStorage(): void {
  if (!leadMemoryStorageAvailable()) return;
  try {
    localStorage.removeItem(LEAD_MEMORY_KEY);
  } catch {
    /* private mode / storage throwing */
  }
  try {
    localStorage.removeItem(LEAD_MEMORY_LEGACY_KEY);
  } catch {
    /* private mode / storage throwing */
  }
}

export function sanitizeLeadMemory(input: unknown): LeadMemory {
  if (typeof input !== "object" || input === null) return {};
  const src = input as Record<string, unknown>;
  const out: LeadMemory = {};
  for (const k of FIELDS) {
    const v = src[k];
    if (typeof v === "string" && v.trim()) out[k] = v.trim().slice(0, 200);
  }
  return out;
}

/**
 * Read the stored envelope. Returns null (and purges) for anything malformed,
 * legacy, expired, or future-dated. Never applies consent rules — callers do.
 */
export function readLeadMemoryEnvelope(now = Date.now()): LeadMemoryEnvelope | null {
  if (!leadMemoryStorageAvailable()) return null;
  let raw: string | null = null;
  try {
    if (localStorage.getItem(LEAD_MEMORY_LEGACY_KEY)) {
      // Legacy records were written without consent and without an expiry.
      purgeLeadMemoryStorage();
    }
    raw = localStorage.getItem(LEAD_MEMORY_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<LeadMemoryEnvelope>;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      parsed.v !== LEAD_MEMORY_ENVELOPE_VERSION ||
      typeof parsed.savedAt !== "number" ||
      !Number.isFinite(parsed.savedAt) ||
      parsed.savedAt <= 0 ||
      // A clock-skewed or forged future timestamp would extend the lifetime.
      parsed.savedAt > now + 60_000
    ) {
      purgeLeadMemoryStorage();
      return null;
    }
    if (now - parsed.savedAt > LEAD_MEMORY_TTL_MS) {
      purgeLeadMemoryStorage();
      return null;
    }
    return {
      v: LEAD_MEMORY_ENVELOPE_VERSION,
      savedAt: parsed.savedAt,
      data: sanitizeLeadMemory(parsed.data),
    };
  } catch {
    purgeLeadMemoryStorage();
    return null;
  }
}

/**
 * Merge a patch into the stored cache, PRESERVING the original expiry clock.
 * Returns the written envelope, or null when nothing was written.
 */
export function writeLeadMemoryEnvelope(
  patch: LeadMemory,
  now = Date.now(),
): LeadMemoryEnvelope | null {
  if (!leadMemoryStorageAvailable()) return null;
  const existing = readLeadMemoryEnvelope(now);
  const next: LeadMemory = { ...(existing?.data ?? {}), ...sanitizeLeadMemory(patch) };
  if (Object.keys(next).length === 0) return null;
  const envelope: LeadMemoryEnvelope = {
    v: LEAD_MEMORY_ENVELOPE_VERSION,
    // Fixed lifetime: an autosave of an existing cache keeps the first clock.
    savedAt: existing?.savedAt ?? now,
    data: next,
  };
  try {
    localStorage.setItem(LEAD_MEMORY_KEY, JSON.stringify(envelope));
  } catch {
    return null;
  }
  return envelope;
}
